import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of, tap } from 'rxjs';
import { environment } from '@app-environment';
import { ApiCacheService } from './api-cache.service';

export interface EntityDefinitionAttribute {
  name?: string;
  displayName?: string | null;
  businessKey?: boolean;
  indexed?: boolean;
  systemAttributes?: boolean;
  type?: string;
  validValues?: unknown[] | null;
  subFields?: EntityDefinitionAttribute[];
  [key: string]: unknown;
}

export interface EntityDefinition {
  entityName?: string;
  displayName?: string;
  description?: string;
  category?: string;
  type?: string;
  attributes?: Record<string, EntityDefinitionAttribute>;
  childEntities?: EntityDefinition[];
  [key: string]: unknown;
}

export type EntityDefinitionsResponse = Record<string, EntityDefinition> | Array<Record<string, unknown>> | {
  data?: Record<string, EntityDefinition>;
  entityDefinitions?: Record<string, EntityDefinition>;
  [key: string]: unknown;
};

export interface NamedEntityDefinition {
  name: string;
  definition: EntityDefinition;
}

/** Loads the entity catalog once and shares it across the application. */
@Injectable({ providedIn: 'root' })
export class EntityDefinitionsService {
  private readonly url = `${environment.URL.replace(/\/$/, '')}/spriced/platform/getAllEntityDefinitions`;
  private readonly cacheKey = ApiCacheService.key('entity-definitions', this.url);

  constructor(
    private readonly http: HttpClient,
    private readonly cache: ApiCacheService,
  ) {}

  load(): Observable<EntityDefinitionsResponse> {
    const cached = this.cache.get<EntityDefinitionsResponse>(this.cacheKey);
    if (cached) return of(cached);

    return this.cache.getOrLoad(
      this.cacheKey,
      () => this.http.get<EntityDefinitionsResponse>(this.url).pipe(
        tap((response) => this.cache.set(this.cacheKey, response)),
      ),
    );
  }

  extract(response: EntityDefinitionsResponse): NamedEntityDefinition[] {
    const wrapped = response as any;
    const source = wrapped?.entityDefinitions ?? wrapped?.data ?? response;
    const entries: Array<[string, EntityDefinition]> = Array.isArray(source)
      ? source.flatMap((item: any, index: number) => {
          const payload = Array.isArray(item?.payload)
            ? item.payload
            : item?.payload && typeof item.payload === 'object'
              ? [item.payload]
              : [item];
          return payload.map((definition: EntityDefinition, payloadIndex: number) => [
            String((definition as any)?.entityName
              ?? (definition as any)?.entity
              ?? item?.entity_name
              ?? `${index}-${payloadIndex}`),
            definition,
          ] as [string, EntityDefinition]);
        })
      : Object.entries(source || {}) as Array<[string, EntityDefinition]>;

    return entries
      .map(([key, definition]) => {
        const normalized = this.normalizeDefinition(definition, key);
        return {
          name: String(normalized.entityName || key).trim(),
          definition: normalized,
        };
      })
      .filter(({ name, definition }) =>
        !!name && !!definition && typeof definition === 'object',
      );
  }

  private normalizeDefinition(definition: EntityDefinition, fallbackName: string): EntityDefinition {
    const raw = definition as any;
    const attributes = raw?.attributes && !Array.isArray(raw.attributes)
      ? raw.attributes
      : Object.fromEntries(
          (Array.isArray(raw?.attributesList) ? raw.attributesList : [])
            .filter((attribute: any) => !!String(attribute?.name || '').trim())
            .map((attribute: EntityDefinitionAttribute) => [String(attribute.name), attribute]),
        );
    const childEntities = (Array.isArray(raw?.childEntities) ? raw.childEntities : [])
      .map((child: EntityDefinition, index: number) =>
        this.normalizeDefinition(child, String((child as any)?.entityName ?? (child as any)?.entity ?? index)),
      );

    return {
      ...raw,
      entityName: String(raw?.entityName ?? raw?.entity ?? fallbackName).trim(),
      attributes,
      childEntities,
    };
  }

  async preload(): Promise<void> {
    try {
      await firstValueFrom(this.load());
    } catch (error) {
      console.error('[ENTITY_DEFINITIONS_LOAD_FAILED]', error);
    }
  }
}
